import apiClient from '@/lib/api-client'
import type {
  CoachAutosaveDraft,
  CoachAvailabilityOverride,
  CoachAvailabilityOverrideWrite,
  CoachBackground,
  CoachCredential,
  CoachCredentialCatalogEntry,
  CoachExpertiseWrite,
  CoachNotableClient,
  CoachProfile,
  CoachProfilePatch,
  CoachPublicProfileWrite,
  CoachRoleRecord,
  CoachSessionType,
  CoachSessionTypeWrite,
  CoachThoughtLeadership,
  CoachTrack,
  CoachTrackAssignment,
  CoachVetting,
  CoachWeeklyAvailability,
} from '@/types/coach'

export const coachProfileService = {
  async getProfile(): Promise<CoachProfile> {
    const response = await apiClient.get<CoachProfile>('/api/v1/coach/profile')
    return response.data
  },
  async getBackground(): Promise<CoachBackground> {
    return (await apiClient.get<CoachBackground>('/api/v1/coach/profile/background')).data
  },
  async getAutosave(): Promise<CoachAutosaveDraft> {
    return (await apiClient.get<CoachAutosaveDraft>('/api/v1/coach/profile/autosave')).data
  },
  async updateAutosave(payload: CoachAutosaveDraft): Promise<CoachAutosaveDraft> {
    return (await apiClient.patch<CoachAutosaveDraft>('/api/v1/coach/profile/autosave', payload))
      .data
  },
  async searchCredentialCatalog(query: string): Promise<CoachCredentialCatalogEntry[]> {
    return (
      await apiClient.get<CoachCredentialCatalogEntry[]>('/api/v1/coach/credential-catalog', {
        // the caller narrows by credential kind, so fetch a wider page to filter from
        params: { q: query, limit: 50 },
      })
    ).data
  },
  async updateBackground(payload: CoachBackground): Promise<CoachBackground> {
    return (await apiClient.put<CoachBackground>('/api/v1/coach/profile/background', payload)).data
  },
  async createCredential(payload: CoachCredential): Promise<CoachBackground> {
    return (
      await apiClient.post<CoachBackground>('/api/v1/coach/profile/background/credentials', payload)
    ).data
  },
  async updateCredential(id: string, payload: CoachCredential): Promise<CoachBackground> {
    return (
      await apiClient.put<CoachBackground>(
        `/api/v1/coach/profile/background/credentials/${id}`,
        payload,
      )
    ).data
  },
  async deleteCredential(id: string): Promise<CoachBackground> {
    return (
      await apiClient.delete<CoachBackground>(`/api/v1/coach/profile/background/credentials/${id}`)
    ).data
  },
  async createAffiliation(payload: CoachRoleRecord): Promise<CoachBackground> {
    return (
      await apiClient.post<CoachBackground>(
        '/api/v1/coach/profile/background/affiliations',
        payload,
      )
    ).data
  },
  async updateAffiliation(id: string, payload: CoachRoleRecord): Promise<CoachBackground> {
    return (
      await apiClient.put<CoachBackground>(
        `/api/v1/coach/profile/background/affiliations/${id}`,
        payload,
      )
    ).data
  },
  async deleteAffiliation(id: string): Promise<CoachBackground> {
    return (
      await apiClient.delete<CoachBackground>(`/api/v1/coach/profile/background/affiliations/${id}`)
    ).data
  },
  async createTeachingRole(payload: CoachRoleRecord): Promise<CoachBackground> {
    return (
      await apiClient.post<CoachBackground>(
        '/api/v1/coach/profile/background/teaching-roles',
        payload,
      )
    ).data
  },
  async updateTeachingRole(id: string, payload: CoachRoleRecord): Promise<CoachBackground> {
    return (
      await apiClient.put<CoachBackground>(
        `/api/v1/coach/profile/background/teaching-roles/${id}`,
        payload,
      )
    ).data
  },
  async deleteTeachingRole(id: string): Promise<CoachBackground> {
    return (
      await apiClient.delete<CoachBackground>(
        `/api/v1/coach/profile/background/teaching-roles/${id}`,
      )
    ).data
  },
  async createNotableClient(payload: CoachNotableClient): Promise<CoachBackground> {
    return (
      await apiClient.post<CoachBackground>(
        '/api/v1/coach/profile/background/notable-clients',
        payload,
      )
    ).data
  },
  async updateNotableClient(id: string, payload: CoachNotableClient): Promise<CoachBackground> {
    return (
      await apiClient.put<CoachBackground>(
        `/api/v1/coach/profile/background/notable-clients/${id}`,
        payload,
      )
    ).data
  },
  async deleteNotableClient(id: string): Promise<CoachBackground> {
    return (
      await apiClient.delete<CoachBackground>(
        `/api/v1/coach/profile/background/notable-clients/${id}`,
      )
    ).data
  },
  async createThoughtLeadership(payload: CoachThoughtLeadership): Promise<CoachBackground> {
    return (
      await apiClient.post<CoachBackground>(
        '/api/v1/coach/profile/background/thought-leadership',
        payload,
      )
    ).data
  },
  async updateThoughtLeadership(
    id: string,
    payload: CoachThoughtLeadership,
  ): Promise<CoachBackground> {
    return (
      await apiClient.put<CoachBackground>(
        `/api/v1/coach/profile/background/thought-leadership/${id}`,
        payload,
      )
    ).data
  },
  async deleteThoughtLeadership(id: string): Promise<CoachBackground> {
    return (
      await apiClient.delete<CoachBackground>(
        `/api/v1/coach/profile/background/thought-leadership/${id}`,
      )
    ).data
  },
  async getVetting(): Promise<CoachVetting> {
    return (await apiClient.get<CoachVetting>('/api/v1/coach/vetting')).data
  },
  async submitVetting(): Promise<CoachVetting> {
    return (
      await apiClient.post<CoachVetting>('/api/v1/coach/vetting/submit', {
        confirmation: true,
      })
    ).data
  },

  async updateProfile(payload: CoachProfilePatch): Promise<CoachProfile> {
    const response = await apiClient.patch<CoachProfile>('/api/v1/coach/profile', payload)
    return response.data
  },
  async updateTracks(tracks: CoachTrack[]): Promise<CoachTrackAssignment> {
    return (await apiClient.put<CoachTrackAssignment>('/api/v1/coach/profile/tracks', { tracks }))
      .data
  },
  async uploadPhoto(file: File): Promise<{ photo_url: string }> {
    const form = new FormData()
    form.append('file', file)
    return (
      await apiClient.post<{ photo_url: string }>('/api/v1/coach/profile/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data
  },
  async updateExpertise(payload: CoachExpertiseWrite): Promise<CoachProfile> {
    const response = await apiClient.put<CoachProfile>('/api/v1/coach/profile/expertise', payload)
    return response.data
  },
  async updatePublicProfile(payload: CoachPublicProfileWrite): Promise<CoachProfile> {
    const response = await apiClient.put<CoachProfile>('/api/v1/coach/profile/public', payload)
    return response.data
  },
  async getAvailability(): Promise<CoachWeeklyAvailability> {
    const response = await apiClient.get<CoachWeeklyAvailability>(
      '/api/v1/coach/availability/weekly',
      { timeout: 10000 },
    )
    return response.data
  },
  async updateAvailability(
    payload: Pick<CoachWeeklyAvailability, 'timezone' | 'windows'>,
  ): Promise<CoachWeeklyAvailability> {
    const response = await apiClient.put<CoachWeeklyAvailability>(
      '/api/v1/coach/availability/weekly',
      payload,
    )
    return response.data
  },
  async confirmAvailability(): Promise<CoachWeeklyAvailability> {
    return (
      await apiClient.post<CoachWeeklyAvailability>('/api/v1/coach/availability/weekly/confirm')
    ).data
  },
  async getAvailabilityOverrides(
    startDate: string,
    endDate: string,
  ): Promise<CoachAvailabilityOverride[]> {
    return (
      await apiClient.get<CoachAvailabilityOverride[]>('/api/v1/coach/availability/overrides', {
        params: { start_date: startDate, end_date: endDate },
      })
    ).data
  },
  async createAvailabilityOverride(
    payload: CoachAvailabilityOverrideWrite,
  ): Promise<CoachAvailabilityOverride> {
    return (
      await apiClient.post<CoachAvailabilityOverride>(
        '/api/v1/coach/availability/overrides',
        payload,
      )
    ).data
  },
  async deleteAvailabilityOverride(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/coach/availability/overrides/${id}`)
  },
  async getSessionTypes(): Promise<CoachSessionType[]> {
    const response = await apiClient.get<CoachSessionType[]>('/api/v1/coach/session-types')
    return response.data
  },
  async createSessionType(payload: CoachSessionTypeWrite): Promise<CoachSessionType> {
    const response = await apiClient.post<CoachSessionType>('/api/v1/coach/session-types', payload)
    return response.data
  },
  async updateSessionType(id: number, payload: CoachSessionTypeWrite): Promise<CoachSessionType> {
    const response = await apiClient.put<CoachSessionType>(
      `/api/v1/coach/session-types/${id}`,
      payload,
    )
    return response.data
  },
  async deleteSessionType(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/coach/session-types/${id}`)
  },
}
